import { ApiRequests } from '@tableau/data-catalog-client-components';
import { Messages } from '../../codegen/Localize';
import { assertUnreachable } from '../helpers/TypescriptHelpers';
import { Module } from '../Module';
import { GetDatabaseActionsRequest } from '../ServerApi/requests/GetDatabaseActionsRequest';
import { GetDatasourceActionsRequest } from '../ServerApi/requests/GetDatasourceActionsRequest';
import { GetFlowActionsRequest } from '../ServerApi/requests/GetFlowActionsRequest';
import { GetTableActionsRequest } from '../ServerApi/requests/GetTableActionsRequest';
import { GetWorkbookActionsRequest } from '../ServerApi/requests/GetWorkbookActionsRequest';
import { Request } from '../ServerApi/requests/Request';
import { ISendImpactAnalysisEmailsParams, SendImpactAnalysisEmailsRequest } from '../ServerApi/requests/SendImpactAnalysisEmailsRequest';
import { SetColumnDescriptionRequest } from '../ServerApi/requests/SetColumnDescriptionRequest';
import { ISingleIdParams } from '../ServerApi/types/actionParams';
import { IEmptyResult } from '../ServerApi/types/IResponse';
import { TableAllowedActions } from '../ServerApi/types/TableAllowedActions';
import { ServerService } from './ServerService';
import { ToasterService, ToastType } from './ToasterService';

type SetColumnDescriptionArgs = {
  columnId: string;
  description: string;
};

type CanUserSetColumnDescriptionArgs = {
  tableId: string;
};

type CatalogActionsRequestArgs = {
  contentType: 'database' | 'table' | 'flow' | 'workbook' | 'datasource';
  contentId: string;
};

interface CatalogPermissionsResponse {
  sendEmail: boolean;
}

function getContentActionsRequest(args: CatalogActionsRequestArgs): Request<ISingleIdParams, CatalogPermissionsResponse> {
  switch (args.contentType) {
    case 'database': return new GetDatabaseActionsRequest(args.contentId);
    case 'table': return new GetTableActionsRequest(args.contentId);
    case 'flow': return new GetFlowActionsRequest(args.contentId);
    case 'workbook': return new GetWorkbookActionsRequest(args.contentId);
    case 'datasource': return new GetDatasourceActionsRequest(args.contentId);
    default: assertUnreachable(args.contentType);
  }
}

export class DataCatalogApiRequestsService implements ApiRequests {

  public static $inject = [
    'ServerService',
    'ToasterService',
  ];

  constructor(
    private server: ServerService,
    private toaster: ToasterService,
  ) { }

  public readonly sendImpactAnalysisEmailAndShowToast = async (args: ISendImpactAnalysisEmailsParams): Promise<void> => {
    return this.server.sendRequest(new SendImpactAnalysisEmailsRequest(args))
      .then((result: any) => {
        if (result && !result.errors) {
          this.toaster.pop({ type: ToastType.Info, text: Messages.dataCatalogEmailSuccess() });
        }

        if (!result || result.errors) {
          this.toaster.pop({ type: ToastType.Error, text: Messages.unexpectedError() });
        }
      });
  }

  public readonly setColumnDescription = async (args: SetColumnDescriptionArgs): Promise<void> =>  {
    return this.server.sendRequest(new SetColumnDescriptionRequest(args.columnId, args.description))
      .then((result: IEmptyResult) => {
        if (result && !result.errors) {
          this.toaster.pop({ type: ToastType.Info, text: Messages.editDescriptionSuccessfulWithWait() });
          return Promise.resolve();
        } else {
          this.toaster.pop({ type: ToastType.Error, text: Messages.editDescriptionFailure() });
          return Promise.reject();
        }
      });
  }

  public readonly canUserSetColumnDescription = async (args: CanUserSetColumnDescriptionArgs): Promise<boolean> => {
    return this.server.sendRequest(new GetTableActionsRequest(args.tableId))
      .then((result: TableAllowedActions) => {
        if (result) {
          return Promise.resolve(result.setDescription);
        } else {
          return Promise.reject();
        }
      });
  }

  public readonly canUserSendImpactAnalysisEmail = async (args: CatalogActionsRequestArgs): Promise<boolean> => {
    return this.server.sendRequest(getContentActionsRequest(args)).then(r => {
      if (!!r) {
        return Promise.resolve(r.sendEmail);
      } else {
        return Promise.reject();
      }
    });
  }
}

Module.instance().service('DataCatalogApiRequestsService', DataCatalogApiRequestsService);
